import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { Chat, SelectLocationItem } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { AnalysisQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { Divider } from '../presentation/Main';
import { BrowserLocationError } from './BrowserLocationError';
import {
  ActiveChatSessionContext,
  KolSearchResultsContext,
  KolSearchResultsSelectionContext,
} from './context';
import { useSelectedQueryParentIdentifier } from './hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from './hooks/useSubmitQueryMutation';

type Props = {
  initialLocation?: SelectLocationItem;
};

function getLocationName(location: SelectLocationItem) {
  if (location.type === 'browser') {
    return `(${location.longitude}, ${location.latitude})`;
  } else {
    return location.name;
  }
}

export const GenerateRouteButton = (props: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { selected } = useContext(KolSearchResultsSelectionContext);
  const results = useContext(KolSearchResultsContext);
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();
  const { mutateAsync } = useSubmitQueryMutation();

  const [location, setLocation] = useState<SelectLocationItem | null>(props.initialLocation);
  const [locationError, setLocationError] = useState<string | null>(null);
  const { isSupported, getLocation, state } = useBrowserLocation({
    onError: setLocationError,
    onSuccess: (result) =>
      setLocation({ type: 'browser', latitude: result.latitude, longitude: result.longitude }),
  });

  const buildKols = useCallback(() => {
    return selected
      .filter((kolId) => {
        const kol = results.find((r) => r.id === kolId);
        return kol && kol.id !== chat.filterOverrides.location?.kolId;
      })
      .map((kolId) => ({ kolId }));
  }, [chat.filterOverrides.location?.kolId, results, selected]);

  const onClick = useCallback(async () => {
    // Get origin location - either from filterOverrides or user input
    let origin: Chat.FilterItem['location'] | null;
    if (chat.filterOverrides.location) {
      origin = chat.filterOverrides.location;
    } else if (location) {
      origin = {
        latitude: location.latitude,
        longitude: location.longitude,
        address: getLocationName(location),
        kolId: location.type === 'hcp' ? Number(location.id) : null,
      };
    } else {
      setErrorMessage(`No location found`);
      return;
    }

    const startingLocation: Chat.QueryState.SmartRouting['startingLocation'] = origin.kolId
      ? {
          type: 'hcp',
          id: origin.kolId,
          name: origin.address ?? '',
          latitude: origin.latitude,
          longitude: origin.longitude,
        }
      : {
          type: 'search',
          id: '',
          name: origin.address ?? '',
          latitude: origin.latitude,
          longitude: origin.longitude,
        };

    const smartRoutingState: Chat.QueryState.SmartRouting = {
      status: 'submit-route',
      startingLocation,
      selectedHcps: buildKols().map((kol) => kol.kolId),
      routingResult: null,
      hcpId: null,
      hideLocationForm: true,
    };

    try {
      const identifier = createId();
      const parentQueryIdentifier = getSelectedQueryParentIdentifier();

      await mutateAsync({
        action: {
          type: ChatActionType.SmartRouting,
          payload: {
            smartRoutingState,
          },
        },
        identifier,
        parentQueryIdentifier,
        query: 'Generate a route with my selected HCPs',
      });
    } catch (error) {
      console.error('Failed to submit route:', error);
      setErrorMessage('Failed to generate route. Please try again.');
    }
  }, [buildKols, chat.filterOverrides.location, location, getSelectedQueryParentIdentifier, mutateAsync]);

  const disabled = useMemo(() => selected.length < 2, [selected.length]);

  const selectedLocation = useMemo(() => {
    if (!location) return null;

    return {
      latitude: location.latitude,
      longitude: location.longitude,
      name: getLocationName(location),
      id:
        location.type === 'browser' ? `${location.latitude}, ${location.longitude}` : location.id?.toString(),
    };
  }, [location]);

  return (
    <>
      {errorMessage && (
        <StartPointSection>
          <Divider />
          <ErrorContainer>
            Please select a starting location for your route before generating a route.
          </ErrorContainer>
          <LocationInputRow>
            <SelectLocation
              styles={{
                container: (baseStyles) => ({ ...baseStyles, width: '400px' }),
              }}
              value={selectedLocation}
              onChange={(value) => {
                // Convert simple LocationItem to discriminated union
                setLocation({
                  type: 'search',
                  id: value.id,
                  name: value.name,
                  latitude: value.latitude,
                  longitude: value.longitude,
                });
              }}
            />
          </LocationInputRow>
          {isSupported && (
            <GetLocationSection>
              <GetLocationButton onClick={getLocation}>Share my current location</GetLocationButton>
              {state === 'error' && <BrowserLocationError errorMessage={locationError} />}
            </GetLocationSection>
          )}
        </StartPointSection>
      )}
      <Tooltip title={disabled ? 'Please select at least 2 HCPs to generate a route.' : ''} placement="top">
        <HintWrapper>
          <AnalysisQueryHint disabled={disabled} value={'Generate Route'} onClick={onClick} />
        </HintWrapper>
      </Tooltip>
    </>
  );
};

const StartPointSection = styled.div({
  '> *': {
    marginTop: 10,
  },
});

const LocationInputRow = styled.div({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
});

const HintWrapper = styled.div({
  marginTop: 15,
  display: 'inline-flex',
});

const ErrorContainer = styled.div({
  marginTop: 10,
  fontSize: 14,
});

const GetLocationSection = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 5,
});

const GetLocationButton = styled.div(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  fontSize: 14,
}));
